import { connect } from 'react-redux';
import { useState, useEffect, useRef } from 'react';

import { SET_EDIT_PROJECT_MEMBERS_DIALOGUE_ID, SiteboardApiValidator, ADD_PROJECT_MEMBER, DELETE_PROJECT, SET_EDIT_PROJECT_DIALOGUE_ID, } from '../actions/types';
import { siteboardAction, setDeleteDialogeProjectId, setPath, } from '../actions/defaultActions';
import { keyCountInObject } from '../Util';
import PushButton from './PushButton';

// icon imports
import {
  MapPin,
  Settings,
  Hash,
  FileText,
} from 'lucide-react';
import Logo from '../SiteBoard_Logo';
import PrintIcon from '../icons/printIcon';
import EmailIcon from '../icons/emailIcon';
import MessagesIcon from '../icons/messagesIcon';
import DotsIcon from '../icons/dotsIcon';
import GlobeIcon from '../icons/globeIcon';
import AddIcon from '../icons/addIcon';
import GantChartIcon from '../icons/gantChartIcon';
import DashboardIcon from '../icons/dashboardIcon';
import TrashIcon from '../icons/trashIcon';
import TeamIcon from '../icons/teamIcon';
import SettingsIcon from '../icons/settingsIcon';
import LocationIcon from '../icons/locationIcon';

const ProjectTile = ({ _userId, _subscriptions, project, setPath, setDeleteDialogeProjectId, siteboardAction }) => {
  const [menuShowing, setMenuShowing] = useState(false);
  const isTouchDevice = useRef(false);
  const containerRef = useRef(null);
  const userRole = _userId in project.members ? project.members[_userId].role : '';
  const memberCountInSelectedProject = (keyCountInObject(project.members) + keyCountInObject(project.pendingMembers));
  const selectedSubscription = _subscriptions[project.subscriptionId];

  // Deselect activity if mouse is clicked anywhere outside it
  // TODO: do this for touch devices too
  useEffect(() => {
    const onMouseDown = (event) => {
     try {
       let element = event.target;
       do {
         if(element === containerRef.current) return;
         element = element.parentNode;
       } while(element.tagName !== 'body' && element.tagName !== 'BODY');
       setMenuShowing(false);
     } catch(error) {}
    };

    window.addEventListener('mousedown', onMouseDown);
    return () => { // remove listener when component unloads
      window.removeEventListener('mousedown', onMouseDown);
    };
  }, []);

  return (
    <div 
      ref={containerRef}
      className='relative text-white overflow:hidden shadow-[0_20px_60px_rgba(0,0,0,0.2)] hover:shadow-[0_12px_35px_rgba(249,169,40,0.08)]'
      onMouseEnter={() => { 
        if(isTouchDevice.current) return;
        setMenuShowing(true); 
      }}
      onMouseLeave={() => {
        if(isTouchDevice.current) return;
        setMenuShowing(false); 
      }} 
      onClick={() => { 
        setTimeout(() => { setMenuShowing(!menuShowing); }, 50); 
      }} 
      onTouchStart = {(event) => {
        // detect touches devices with this event
        isTouchDevice.current = true;
      }}
    >
       <button className={`relative flex flex-col bg-neutral-700 border border-white/10 ${menuShowing ? 'outline outline-[#f9a928] outline-offset-[-2px] pointer-events-none opacity-0' : ''} p-4 min-h-[16em] w-full rounded-2xl h-full`}>
         <div className='flex justify-between items-start w-full'>
           {(typeof project.jobNumber === 'string' && project.jobNumber.length > 0) &&
           <div className='flex items-center justify-end w-full gap-x-1 text-[#f9a928]'>
             <Hash size={16} color='#f9a928' /> {project.jobNumber}
           </div>
           }
         </div>
         <header className='w-full text-center font-bold text-white text-[1.6em] leading-[1.2em] tracking-tight'>{project.name}</header>

         <div className='grow w-full flex items-center justify-center min-h-[3em] font-medium text-white/60 whitespace-pre-line leading-[1.4em] mt-2 mb-[10px] max-w-full'>
           {(typeof project.address === 'string' && project.address.length > 0) &&
             <div className='gap-1 py-2'>
               <div className='inline-block align-middle'>
                 <MapPin
                   size={16}
                   className="mt-0.5 shrink-0 text-[#f9a928] relative left-[-5px] top-[-2px]"
                 />
               </div>
               {project.address}
             </div>
           }
         </div>

         <div className='flex items-center gap-x-1 text-white self-start'>
           <label className='text-sm'>Project Members:</label>
            <div className='flex items-center gap-1.5 font-bold'>
              {memberCountInSelectedProject}
            </div>
         </div>
         <div className='flex items-center gap-x-1 text-white self-start'>
           <label className='text-sm'>Distribution Contacts:</label>
           <div className='flex items-center gap-1.5 font-bold'>
             {project.contactCount}
           </div>
         </div>
         {(typeof project.superintendent === 'string' && project.superintendent.length > 0) &&
           <div className='flex items-center gap-x-1 text-white self-start'>
             <label className='text-sm'>Superintendent:</label>
             <div className='w-full text-[1.1em] font-bold text-left leading-[1em]'>{project.superintendent || '?'}</div>
           </div>
         }
         {/*userRole.length > 0 &&
           <div className='flex items-center gap-x-1 text-white self-start'>
             <label className='text-sm whitespace-nowrap'>Your Role:</label>
             <div className='w-full text-[1.1em] font-bold text-left leading-[1em]'>{userRole}</div>
           </div>
         */}
       </button>
       {/* Hover shade */}
       <div className={`absolute top-0 left-0 w-full h-full bg-[#111] border border-[#f9a928]/30 ${menuShowing ? 'opacity-80' : 'opacity-0'} rounded-2xl transition-all duration-200`}></div>
       {/* Popup options */}
       <div className={`absolute ${menuShowing ? 'flex flex-col ' : 'hidden pointer-events-none'} w-full h-full justify-center items-center top-0 left-0 transition-all p-4 animate-up-fade`}>
         <header className='text-[1.3em] leading-[1.3em] font-bold text-center mb-4'>{project.name}</header>
         <div className='grow grid grid-cols-2 w-full gap-1.5 justify-center items-center'>
           {/* Project 3-weeks button */}
           <PushButton 
             textColor={'white'}
             bgColor='bg-neutral-800'
             hoverBgColor='bg-neutral-700'
             className='group/tile'
             onClick={() => { setPath('projectId#' + project.id + '#schedule'); }}
           >
             <div className='flex flex-col gap-1 justify-center items-center gap-y-1 p-2 w-full h-full '>
               <div className='relative group-hover/tile:animate-bounce motion-reduce:animate-none'>
                 <div className='absolute top-0 left-0 opacity-0 group-hover/tile:opacity-100 transition-all duration-300'><GantChartIcon size='26' color='#8060ff' /></div>
                 <div className='opacity-100 group-hover/tile:opacity-0 transition-all duration-300'><GantChartIcon size='26' color='#fff' /></div>
               </div>
               <p className='font-bold leading-[1em]'>Schedule</p>
             </div>
           </PushButton>

           {/* Daily Reports button */}
           {process.env.REACT_APP_DAILY_REPORTS_ENABLE === 'true' &&
             <PushButton 
               textColor={'white'}
               bgColor='bg-neutral-800'
               hoverBgColor='bg-neutral-700'
               className='group/tile'
               onClick={() => { 
                 setPath(`projectId#${project.id}#dailyReports`);
               }}
             >
               <div className='flex flex-col gap-1 justify-center items-center gap-y-1 p-2 w-full h-full '>
                 <div className='relative group-hover/tile:animate-bounce motion-reduce:animate-none'>
                   <div className='absolute top-0 left-0 opacity-0 group-hover/tile:opacity-100 transition-all duration-300'><FileText size={26} className='text-emerald-500' /></div>
                   <div className='opacity-100 group-hover/tile:opacity-0 transition-all duration-300'><FileText size={26} className='text-white' /></div>
                 </div>
                 <p className='font-bold leading-[1em]'>Daily Report</p>
               </div>
             </PushButton>
           }

           {/* Project Settings button */}
           <PushButton 
             textColor={'white'}
             bgColor='bg-neutral-800'
             hoverBgColor='bg-neutral-700'
             className='group/tile'
             onClick={() => { 
               siteboardAction(SET_EDIT_PROJECT_DIALOGUE_ID, { projectId: project.id });
             }}
           >
             <div className='flex flex-col gap-1 justify-center items-center gap-y-1 p-2 w-full h-full'>
               <Settings
                 size={26}
                 strokeWidth={1.8}
                 className="text-white group-hover/tile:animate-[slow-step-spin_8s_ease-in-out_infinite] motion-reduce:animate-none"
               />
               <div className='font-bold leading-[1em]'>Settings</div>
             </div>
           </PushButton> 

           {/* Add members button */}
           {/*SiteboardApiValidator[ADD_PROJECT_MEMBER].isUserIdAuthorized(_userId, selectedSubscription, project) &&
             <PushButton 
               textColor={'white'}
               bgColor={'#232323'}
               hoverBgColor={'#333'}
               className='group/tile'
               onClick={() => { siteboardAction(SET_EDIT_PROJECT_MEMBERS_DIALOGUE_ID, { projectId: project.id }); }}
             >
               <div className='flex flex-col gap-1 justify-center items-center gap-y-1 p-2 w-full h-full'>
                 <div className='relative group-hover/tile:animate-bounce motion-reduce:animate-none'>
                   <div className='absolute top-0 left-0 opacity-0 group-hover/tile:opacity-100 transition-all duration-300'><TeamIcon size='26' color='#f9a928' /></div>
                   <div className='opacity-100 group-hover/tile:opacity-0 transition-all duration-300'><TeamIcon size='26' color='#fff' />
                   </div>
                 </div>
                 <p className='font-bold leading-[1em]'>Members</p>
               </div>
             </PushButton>
           */}
           {/* Delete project button */}
           {/*SiteboardApiValidator[DELETE_PROJECT].isUserIdAuthorized(_userId, selectedSubscription, project) &&
             <PushButton 
               textColor={'white'}
               bgColor={'#232323'}
               hoverBgColor={'#333333'}
               className='group/tile'
               onClick={() => { setDeleteDialogeProjectId(project.id); }}
             >
               <div className='flex flex-col gap-1 justify-center items-center gap-y-1 p-2 w-full h-full'>
                 <div className='relative group-hover/tile:animate-bounce motion-reduce:animate-none'>
                   <div className='absolute top-0 left-0 opacity-0 group-hover/tile:opacity-100 transition-all duration-300'><TrashIcon size='26' color='#f12035' /></div>
                   <div className='opacity-100 group-hover/tile:opacity-0 transition-all duration-300'><TrashIcon size='26' color='#fff' />
                   </div>
                 </div>
                 <p className='font-bold leading-[1em]'>Delete Project</p>
               </div>
             </PushButton>
           */}
         </div>
         {/*<div className='flex justify-end w-full mt-1.5 hidden'>
           <button className='p-2 bg-[#232323] hover:bg-[#333] rounded-lg'><DotsIcon size='1.2em' /></button>
         </div>*/}
       </div>
    </div>
  );
}

const mapStateToProps = state => { return { // define redux state that will be passed in as props
  _userId: state.localPersistent.userId,
  _subscriptions: state.serverSynced.subscriptions,
}};
export default connect(mapStateToProps, { siteboardAction, setDeleteDialogeProjectId, setPath, })(ProjectTile);
